import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Navbar } from "../../components/Navbar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL } from "../../config/api";
import {
  Users,
  Building2,
  UserPlus,
  Search,
  RefreshCw,
  Loader2,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  Trash2,
  Plus,
  ArrowRight,
  Building,
  Linkedin,
  Facebook,
  Instagram,
  MessageCircle,
  Cake,
  FileText,
  Link2,
  Link,
  Send,
  Copy,
  ExternalLink,
  CreditCard,
  Handshake,
  Globe,
  Tag,
  X,
  Check,
  Download,
  Upload,
  Folder,
  Target,
  ChevronRight,
} from "lucide-react";


export default function CRMPage() {
  const [activeTab, setActiveTab] = useState("contacts");  // Changed from "pending" to show data immediately
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [exactMatchSearch, setExactMatchSearch] = useState(false); // Word-boundary search for precise matching
  const [selectedContact, setSelectedContact] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  const [showEditContactDialog, setShowEditContactDialog] = useState(false);
  const [showEditCompanyDialog, setShowEditCompanyDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companySearch, setCompanySearch] = useState([]);
  const [showKYCDialog, setShowKYCDialog] = useState(false);
  const [kycForm, setKycForm] = useState({ company_name: "", contact_email: "", contact_name: "", notes: "" });
  const [kycSending, setKycSending] = useState(false);
  const [kycLink, setKycLink] = useState(null);
  
  // Document upload state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "", linked_company_id: "", linked_contact_id: "", category: "general", vertical: "", doc_tag: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [folderSearch, setFolderSearch] = useState({});
  const [folderDragActive, setFolderDragActive] = useState(null); // Track which folder has drag active
  const [showQuickUpload, setShowQuickUpload] = useState(false);
  const [quickUploadFile, setQuickUploadFile] = useState(null);
  const [quickUploadCategory, setQuickUploadCategory] = useState(null);
  const [quickUploadName, setQuickUploadName] = useState("");

  // Company Documents (per vertical) — inline drag-drop state
  // Each vertical card has its own tag dropdown + drag state. We keep this
  // local instead of routing through the dialog so that uploading 4 different
  // docs to 4 verticals is a single drop each (matches the PF Documents UX).
  const [companyDocTagDraft, setCompanyDocTagDraft] = useState({
    europacific: "company_registry", cargo_cfs: "company_registry",
    ep_holding: "company_registry", ep_as: "company_registry",
  });
  const [companyDocDragActive, setCompanyDocDragActive] = useState(null); // vertical key being dragged onto
  const [companyDocUploading, setCompanyDocUploading] = useState(null);   // vertical key currently uploading
  
  // Pantheon Import/Export state
  const [showPantheonDialog, setShowPantheonDialog] = useState(false);
  const [pantheonImporting, setPantheonImporting] = useState(false);
  const [pantheonFile, setPantheonFile] = useState(null);
  const [pantheonImportMode, setPantheonImportMode] = useState("skip");
  const [pantheonStats, setPantheonStats] = useState(null);
  
  // Contact source filter
  const [contactSourceFilter, setContactSourceFilter] = useState("all");
  const [showImportEmailsDialog, setShowImportEmailsDialog] = useState(false);
  const [importingEmails, setImportingEmails] = useState(false);
  const [emailImportSource, setEmailImportSource] = useState("customer_advisory");
  
  // Email Lists tab state
  const [emailSourcesStats, setEmailSourcesStats] = useState(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(false);
  
  // Groups tab state
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [memberCompanySearch, setMemberCompanySearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  
  const { getAuthHeaders } = useAuth();

  const [confirmForm, setConfirmForm] = useState({
    first_name: "",
    last_name: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    whatsapp: "",
    viber: "",
    position: "",
    company_id: "",
    company_name: "",
    create_company: false,
    birthday: "",
    linkedin: "",
    facebook: "",
    instagram: "",
    notes: "",
  });

  const [newContact, setNewContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    whatsapp: "",
    viber: "",
    position: "",
    company_id: "",
    company_name: "",
    birthday: "",
    linkedin: "",
    facebook: "",
    instagram: "",
    notes: "",
  });

  const [newCompany, setNewCompany] = useState({
    name: "",
    domain: "",
    address: "",
    city: "",
    country: "",
    vat_number: "",
    phone: "",
    industry: "",
    pantheon_code: "",
  });

  // Tag management state
  const [newTagInput, setNewTagInput] = useState("");
  const [tagPopoverOpen, setTagPopoverOpen] = useState(null); // company id or null
  const [contactTagPopoverOpen, setContactTagPopoverOpen] = useState(null); // contact id or null
  const [contactAssignPopoverOpen, setContactAssignPopoverOpen] = useState(null); // contact id or null
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [companySearchResults, setCompanySearchResults] = useState([]);

  // Common tag suggestions
  const TAG_SUGGESTIONS = ["Korea", "China", "USA", "Europe", "VIP", "Partner", "Agent", "Carrier", "Shipper", "Consignee"];

  // Update company tags
  const updateCompanyTags = async (companyId, tags) => {
    try {
      await axios.patch(
        `${API_URL}/crm/companies/${companyId}`,
        { tags },
        { headers: getAuthHeaders() }
      );
      // Update local state
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, tags } : c));
      toast.success("Tags updated");
    } catch (error) {
      toast.error("Failed to update tags");
      console.error(error);
    }
  };

  // Add tag to company
  const addTagToCompany = (companyId, tag) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return;
    const currentTags = company.tags || [];
    if (!currentTags.includes(tag)) {
      updateCompanyTags(companyId, [...currentTags, tag]);
    }
    setNewTagInput("");
  };

  // Remove tag from company
  const removeTagFromCompany = (companyId, tag) => {
    const company = companies.find(c => c.id === companyId);
    if (!company) return;
    const currentTags = company.tags || [];
    updateCompanyTags(companyId, currentTags.filter(t => t !== tag));
  };

  // Update contact tags
  const updateContactTags = async (contactId, tags) => {
    try {
      await axios.patch(
        `${API_URL}/crm/contacts/${contactId}`,
        { tags },
        { headers: getAuthHeaders() }
      );
      // Update local state
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, tags } : c));
      toast.success("Tags updated");
    } catch (error) {
      toast.error("Failed to update tags");
      console.error(error);
    }
  };

  // Add tag to contact
  const addTagToContact = (contactId, tag) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const currentTags = contact.tags || [];
    if (!currentTags.includes(tag)) {
      updateContactTags(contactId, [...currentTags, tag]);
    }
    setNewTagInput("");
  };

  // Remove tag from contact
  const removeTagFromContact = (contactId, tag) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const currentTags = contact.tags || [];
    updateContactTags(contactId, currentTags.filter(t => t !== tag));
  };

  // Search companies for assigning contact
  const searchCompaniesForAssign = async (query) => {
    if (!query || query.length < 2) {
      setCompanySearchResults([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/crm/companies?search=${encodeURIComponent(query)}&limit=10`,
        { headers: getAuthHeaders() }
      );
      setCompanySearchResults(response.data.companies || []);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  // Assign contact to company
  const assignContactToCompany = async (contactId, companyId, companyName) => {
    try {
      await axios.patch(
        `${API_URL}/crm/contacts/${contactId}`,
        { company_id: companyId, company_name: companyName },
        { headers: getAuthHeaders() }
      );
      // Update local state
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, company_id: companyId, company_name: companyName } : c));
      toast.success(`Assigned to ${companyName}`);
      setContactAssignPopoverOpen(null);
      setCompanySearchQuery("");
      setCompanySearchResults([]);
    } catch (error) {
      toast.error("Failed to assign contact");
      console.error(error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchPendingContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/contacts/pending`, { headers: getAuthHeaders() });
      setPendingContacts(response.data.contacts);
    } catch (error) {
      console.error("Failed to fetch pending:", error);
    }
  };

  // Pagination state
  const [contactsPage, setContactsPage] = useState(1);
  const [companiesPage, setCompaniesPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const pageSize = 50;

  // Sort state for contacts (by domain)
  const [contactSortBy, setContactSortBy] = useState("created_at");
  const [contactSortOrder, setContactSortOrder] = useState("desc");

  // Sort state for companies
  const [companySortBy, setCompanySortBy] = useState("name");
  const [companySortOrder, setCompanySortOrder] = useState("asc");

  const fetchContacts = async (page = 1) => {
    try {
      const skip = (page - 1) * pageSize;
      const params = { 
        limit: pageSize, 
        skip,
        sort_by: contactSortBy,
        sort_order: contactSortOrder
      };
      if (searchQuery) params.search = searchQuery;
      if (contactSourceFilter && contactSourceFilter !== "all") {
        params.source = contactSourceFilter;
      }
      const response = await axios.get(`${API_URL}/crm/contacts`, { headers: getAuthHeaders(), params });
      setContacts(response.data.contacts);
      setContactsTotal(response.data.total || response.data.contacts.length);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    }
  };

  const fetchCompanies = async (page = 1) => {
    try {
      const skip = (page - 1) * pageSize;
      const params = { 
        limit: pageSize, 
        skip,
        sort_by: companySortBy,
        sort_order: companySortOrder
      };
      if (searchQuery) {
        params.search = searchQuery;
        params.exact_match = exactMatchSearch;
      }
      const response = await axios.get(`${API_URL}/crm/companies`, { headers: getAuthHeaders(), params });
      setCompanies(response.data.companies);
      setCompaniesTotal(response.data.total || response.data.companies.length);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const searchCompanies = async (query) => {
    if (query.length < 2) {
      setCompanySearch([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/crm/companies/search`, {
        headers: getAuthHeaders(),
        params: { q: query }
      });
      setCompanySearch(response.data.companies);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  // Document upload functions
  const fetchUploadedDocs = async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/uploaded-documents`, { headers: getAuthHeaders() });
      setUploadedDocs(response.data.documents || []);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      if (!uploadForm.name) {
        setUploadForm(prev => ({ ...prev, name: e.dataTransfer.files[0].name.replace(/\.[^/.]+$/, "") }));
      }
    }
  };

  // Folder-specific drag handlers
  const handleFolderDragEnter = (e, folderKey) => {
    e.preventDefault();
    e.stopPropagation();
    setFolderDragActive(folderKey);
  };

  const handleFolderDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the folder completely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setFolderDragActive(null);
    }
  };

  const handleFolderDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFolderDrop = (e, folderKey) => {
    e.preventDefault();
    e.stopPropagation();
    setFolderDragActive(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const defaultName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      setQuickUploadFile(file);
      setQuickUploadCategory(folderKey);
      setQuickUploadName(defaultName);
      setShowQuickUpload(true);
    }
  };

  const handleCompanyDocUpload = async (verticalKey, file, docTag) => {
    if (!file) return;
    if (!docTag) {
      toast.error("Please pick a document type from the dropdown first.");
      return;
    }
    setCompanyDocUploading(verticalKey);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name.replace(/\.[^/.]+$/, ""));
      formData.append("category", "company_docs");
      formData.append("vertical", verticalKey);
      formData.append("doc_tag", docTag);
      await axios.post(`${API_URL}/crm/upload-document`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded to ${verticalKey.replace("_", " ")}`);
      fetchUploadedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setCompanyDocUploading(null);
    }
  };


  const handleQuickUpload = async () => {
    if (!quickUploadFile || !quickUploadName) {
      toast.error("Please enter a document name");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", quickUploadFile);
      formData.append("name", quickUploadName);
      formData.append("category", quickUploadCategory);
      
      await axios.post(`${API_URL}/crm/upload-document`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded successfully!");
      setShowQuickUpload(false);
      setQuickUploadFile(null);
      setQuickUploadCategory(null);
      setQuickUploadName("");
      fetchUploadedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      if (!uploadForm.name) {
        setUploadForm(prev => ({ ...prev, name: e.target.files[0].name.replace(/\.[^/.]+$/, "") }));
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !uploadForm.name) {
      toast.error("Please select a file and enter a name");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", uploadForm.name);
      formData.append("category", uploadForm.category);
      if (uploadForm.linked_company_id) {
        formData.append("linked_company_id", uploadForm.linked_company_id);
      }
      if (uploadForm.linked_contact_id) {
        formData.append("linked_contact_id", uploadForm.linked_contact_id);
      }
      if (uploadForm.vertical) {
        formData.append("vertical", uploadForm.vertical);
      }
      if (uploadForm.doc_tag) {
        formData.append("doc_tag", uploadForm.doc_tag);
      }
      
      await axios.post(`${API_URL}/crm/upload-document`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded successfully!");
      setShowUploadDialog(false);
      setSelectedFile(null);
      setUploadForm({ name: "", linked_company_id: "", linked_contact_id: "", category: "general", vertical: "", doc_tag: "" });
      fetchUploadedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await axios.delete(`${API_URL}/crm/uploaded-documents/${docId}`, { headers: getAuthHeaders() });
      toast.success("Document deleted");
      fetchUploadedDocs();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchPendingContacts(), fetchContacts(), fetchCompanies(), fetchUploadedDocs()]);
    setLoading(false);
  };

  // Fetch email sources statistics
  const fetchEmailSourcesStats = async () => {
    setLoadingEmailStats(true);
    try {
      const response = await axios.get(`${API_URL}/crm/email-sources/stats`, { headers: getAuthHeaders() });
      setEmailSourcesStats(response.data);
    } catch (error) {
      console.error("Failed to fetch email sources stats:", error);
    } finally {
      setLoadingEmailStats(false);
    }
  };

  // Groups tab functions
  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await axios.get(`${API_URL}/customer-groups`, { headers: getAuthHeaders() });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error("Failed to load groups:", error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const response = await axios.get(`${API_URL}/customer-groups/${groupId}`, { headers: getAuthHeaders() });
      setSelectedGroup(response.data);
    } catch (error) {
      toast.error("Failed to load group details");
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error("Group name is required");
      return;
    }
    
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await axios.put(
          `${API_URL}/customer-groups/${editingGroup.id}`,
          { name: groupName.trim(), description: groupDescription.trim() || null },
          { headers: getAuthHeaders() }
        );
        toast.success("Group updated");
      } else {
        await axios.post(
          `${API_URL}/customer-groups`,
          { name: groupName.trim(), description: groupDescription.trim() || null },
          { headers: getAuthHeaders() }
        );
        toast.success("Group created");
      }
      
      setShowCreateGroupDialog(false);
      setGroupName("");
      setGroupDescription("");
      setEditingGroup(null);
      fetchGroups();
      if (selectedGroup) fetchGroupDetails(selectedGroup.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save group");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Delete this group? Companies will not be deleted.")) return;
    
    try {
      await axios.delete(`${API_URL}/customer-groups/${groupId}`, { headers: getAuthHeaders() });
      toast.success("Group deleted");
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const searchMemberCompanies = async (query) => {
    if (!query || query.length < 2) {
      setMemberSearchResults([]);
      return;
    }
    
    setSearchingMembers(true);
    try {
      const response = await axios.get(
        `${API_URL}/crm/companies/search?q=${encodeURIComponent(query)}&limit=10`,
        { headers: getAuthHeaders() }
      );
      // Filter out companies already in the group
      const existingIds = selectedGroup?.member_companies?.map(m => m.company_id) || [];
      setMemberSearchResults((response.data.companies || []).filter(c => !existingIds.includes(c.id)));
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setSearchingMembers(false);
    }
  };

  const handleAddMember = async (company) => {
    setAddingMember(true);
    try {
      await axios.post(
        `${API_URL}/customer-groups/${selectedGroup.id}/members`,
        { 
          company_id: company.id,
          company_name: company.name,
          country: company.country || "",
          accounting_ref: company.accounting_ref || ""
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`${company.name} added to group`);
      fetchGroupDetails(selectedGroup.id);
      fetchGroups();
      setMemberCompanySearch("");
      setMemberSearchResults([]);
      setShowAddMemberDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (companyId) => {
    try {
      await axios.delete(
        `${API_URL}/customer-groups/${selectedGroup.id}/members/${companyId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Member removed");
      fetchGroupDetails(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const openEditGroupDialog = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || "");
    setShowCreateGroupDialog(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === "contacts") fetchContacts(contactsPage);
    if (activeTab === "companies") fetchCompanies(companiesPage);
    if (activeTab === "email-lists") fetchEmailSourcesStats();
    if (activeTab === "groups") fetchGroups();
  }, [searchQuery, exactMatchSearch, activeTab, contactsPage, companiesPage, companySortBy, companySortOrder, contactSortBy, contactSortOrder, contactSourceFilter]);

  // Import emails from various sources
  const handleImportEmails = async () => {
    setImportingEmails(true);
    try {
      const response = await axios.post(
        `${API_URL}/crm/contacts/import-emails`,
        { source: emailImportSource },
        { headers: getAuthHeaders() }
      );
      
      const { imported, skipped, total } = response.data;
      toast.success(`Imported ${imported} contacts (${skipped} skipped) from ${emailImportSource.replace('_', ' ')}`);
      
      fetchContacts();
      setShowImportEmailsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImportingEmails(false);
    }
  };

  const openConfirmDialog = (contact) => {
    setSelectedContact(contact);
    setConfirmForm({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobile: contact.mobile || "",
      whatsapp: contact.whatsapp || "",
      viber: contact.viber || "",
      position: contact.position || "",
      company_id: contact.company_id || contact.suggested_company?.existing_company?.id || "",
      company_name: contact.company_name || contact.suggested_company?.suggested_name || "",
      create_company: false,
      birthday: contact.birthday || "",
      linkedin: contact.linkedin || "",
      facebook: contact.facebook || "",
      instagram: contact.instagram || "",
      notes: contact.notes || "",
    });
    setShowConfirmDialog(true);
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      toast.error("Email is required");
      return;
    }
    try {
      await axios.post(`${API_URL}/crm/contacts`, newContact, { headers: getAuthHeaders() });
      toast.success("Contact created!");
      setShowAddContactDialog(false);
      setNewContact({
        first_name: "", last_name: "", email: "", phone: "", mobile: "",
        whatsapp: "", viber: "", position: "", company_id: "", company_name: "",
        birthday: "", linkedin: "", facebook: "", instagram: "", notes: ""
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create contact");
    }
  };

  const handleConfirmContact = async () => {
    try {
      await axios.post(
        `${API_URL}/crm/contacts/${selectedContact.id}/confirm`,
        confirmForm,
        { headers: getAuthHeaders() }
      );
      toast.success("Contact confirmed!");
      setShowConfirmDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm contact");
    }
  };

  const handleDismissContact = async (contactId) => {
    if (!window.confirm("Dismiss this contact?")) return;
    try {
      await axios.delete(`${API_URL}/crm/contacts/${contactId}`, { headers: getAuthHeaders() });
      toast.success("Contact dismissed");
      loadData();
    } catch (error) {
      toast.error("Failed to dismiss contact");
    }
  };

  const handleCreateCompany = async () => {
    try {
      const response = await axios.post(`${API_URL}/crm/companies`, newCompany, { headers: getAuthHeaders() });
      toast.success("Company created!");
      setShowCompanyDialog(false);
      setNewCompany({ name: "", domain: "", address: "", city: "", country: "", vat_number: "", phone: "", industry: "", pantheon_code: "" });
      fetchCompanies();
      fetchStats();
      // If in confirm dialog, set the new company
      if (showConfirmDialog) {
        setConfirmForm(prev => ({
          ...prev,
          company_id: response.data.id,
          company_name: response.data.name,
          create_company: false
        }));
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      // 409 — duplicate. Offer to open the existing record instead of silently failing.
      if (status === 409 && detail && typeof detail === "object" && detail.existing_id) {
        const msg = detail.message || `Company already exists.`;
        const open = window.confirm(`${msg}\n\nOpen the existing company '${detail.existing_name}'?`);
        if (open) {
          setShowCompanyDialog(false);
          setActiveTab("companies");
          setCompanySearchQuery(detail.existing_name || "");
          fetchCompanies();
          toast.info(`Opened existing: ${detail.existing_name}`);
        }
        return;
      }
      // Pydantic 422 errors come back as a list — render the first message
      let msg = "Failed to create company";
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const fieldPath = (first.loc || []).slice(1).join(".") || "field";
        msg = `${fieldPath}: ${first.msg || "validation error"}`;
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object" && detail.message) {
        msg = detail.message;
      }
      toast.error(msg);
    }
  };

  // Pantheon Import/Export Functions
  const fetchPantheonStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/companies/pantheon-stats`, { headers: getAuthHeaders() });
      setPantheonStats(response.data);
    } catch (error) {
      console.error("Failed to fetch Pantheon stats:", error);
    }
  };

  const handlePantheonImport = async () => {
    if (!pantheonFile) {
      toast.error("Please select a file to import");
      return;
    }
    
    setPantheonImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", pantheonFile);
      
      const response = await axios.post(
        `${API_URL}/crm/companies/import-pantheon?mode=${pantheonImportMode}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      const { imported, updated, skipped, total_rows, errors } = response.data;
      toast.success(`Import complete: ${imported} imported, ${updated} updated, ${skipped} skipped out of ${total_rows} rows`);
      
      if (errors && errors.length > 0) {
        console.warn("Import errors:", errors);
      }
      
      setPantheonFile(null);
      fetchCompanies();
      fetchStats();
      fetchPantheonStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setPantheonImporting(false);
    }
  };

  const handlePantheonExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/companies/export-pantheon`, { headers: getAuthHeaders() });
      
      // Convert to CSV and download
      const { companies } = response.data;
      if (!companies || companies.length === 0) {
        toast.error("No companies to export");
        return;
      }
      
      const headers = Object.keys(companies[0]);
      const csvContent = [
        headers.join(";"),
        ...companies.map(c => headers.map(h => `"${(c[h] || "").toString().replace(/"/g, '""')}"`).join(";"))
      ].join("\n");
      
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pantheon_export_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${companies.length} companies`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // KYC Functions
  const handleSendKYC = async () => {
    if (!kycForm.contact_email) {
      toast.error("Email is required");
      return;
    }
    
    setKycSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/kyc/create-link`,
        kycForm,
        { headers: getAuthHeaders() }
      );
      
      setKycLink(response.data);
      
      if (response.data.email_sent) {
        toast.success(`KYC link sent to ${kycForm.contact_email}!`);
      } else {
        toast.info("KYC link created (email not sent - copy link manually)");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create KYC link");
    } finally {
      setKycSending(false);
    }
  };

  const copyKycLink = () => {
    if (kycLink?.kyc_link) {
      navigator.clipboard.writeText(kycLink.kyc_link);
      toast.success("Link copied to clipboard!");
    }
  };

  const resetKycDialog = () => {
    setShowKYCDialog(false);
    setKycForm({ company_name: "", contact_email: "", contact_name: "", notes: "" });
    setKycLink(null);
  };

  // Send KYC form directly from contact row
  const handleSendKYCForm = (contact) => {
    setKycForm({
      company_name: contact.company_name || "",
      contact_email: contact.email || "",
      contact_name: contact.name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim(),
      notes: ""
    });
    setShowKYCDialog(true);
  };

  // Edit Contact functions
  const openEditContactDialog = (contact) => {
    setEditingContact({
      id: contact.id,
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobile: contact.mobile || "",
      whatsapp: contact.whatsapp || "",
      viber: contact.viber || "",
      position: contact.position || "",
      company_id: contact.company_id || "",
      company_name: contact.company_name || "",
      birthday: contact.birthday || "",
      linkedin: contact.linkedin || "",
      facebook: contact.facebook || "",
      instagram: contact.instagram || "",
      notes: contact.notes || "",
    });
    setShowEditContactDialog(true);
  };

  const handleUpdateContact = async () => {
    if (!editingContact?.id) return;
    try {
      await axios.put(
        `${API_URL}/crm/contacts/${editingContact.id}`,
        editingContact,
        { headers: getAuthHeaders() }
      );
      toast.success("Contact updated!");
      setShowEditContactDialog(false);
      setEditingContact(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update contact");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact permanently?")) return;
    try {
      await axios.delete(`${API_URL}/crm/contacts/${contactId}`, { headers: getAuthHeaders() });
      toast.success("Contact deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // Edit Company functions
  const openEditCompanyDialog = (company) => {
    setEditingCompany({
      id: company.id,
      name: company.name || "",
      domain: company.domain || "",
      address: company.address || "",
      city: company.city || "",
      country: company.country || "",
      vat_number: company.vat_number || "",
      phone: company.phone || "",
      industry: company.industry || "",
    });
    setShowEditCompanyDialog(true);
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany?.id) return;
    try {
      await axios.put(
        `${API_URL}/crm/companies/${editingCompany.id}`,
        editingCompany,
        { headers: getAuthHeaders() }
      );
      toast.success("Company updated!");
      setShowEditCompanyDialog(false);
      setEditingCompany(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update company");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Delete this company permanently?")) return;
    try {
      await axios.delete(`${API_URL}/crm/companies/${companyId}`, { headers: getAuthHeaders() });
      toast.success("Company deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  // Generate agreements
  const [generatingAgreement, setGeneratingAgreement] = useState(null);
  
  const generateCreditAgreement = async (company) => {
    setGeneratingAgreement(`credit-${company.id}`);
    try {
      const response = await axios.post(
        `${API_URL}/crm/companies/${company.id}/generate-credit-agreement`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Credit Agreement generated for ${company.name}`);
      window.open(`/crm-documents?highlight=${response.data.document_id}`, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate Credit Agreement");
    } finally {
      setGeneratingAgreement(null);
    }
  };

  const generateAgencyAgreement = async (company) => {
    setGeneratingAgreement(`agency-${company.id}`);
    try {
      const response = await axios.post(
        `${API_URL}/crm/companies/${company.id}/generate-agency-agreement`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Agency Agreement generated for ${company.name}`);
      window.open(`/crm-documents?highlight=${response.data.document_id}`, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate Agency Agreement");
    } finally {
      setGeneratingAgreement(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between"
          >
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                <span className="text-primary">CRM</span> Contacts
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage contacts and companies
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadData} data-testid="refresh-crm-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => setShowAddContactDialog(true)} data-testid="add-contact-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
              <Button variant="outline" onClick={() => setShowKYCDialog(true)} data-testid="send-kyc-btn">
                <FileText className="h-4 w-4 mr-2" />
                Send KYC Form
              </Button>
              <Button variant="outline" onClick={() => { setShowPantheonDialog(true); fetchPantheonStats(); }} data-testid="pantheon-import-btn">
                <Upload className="h-4 w-4 mr-2" />
                Pantheon
              </Button>
              <Button onClick={() => setShowCompanyDialog(true)} data-testid="add-company-btn">
                <Building2 className="h-4 w-4 mr-2" />
                Add Company
              </Button>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
          >
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-warning/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.pending_review || 0}</p>
                  <p className="text-xs text-muted-foreground">Pending Review</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total_contacts || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Contacts</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-accent/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total_companies || 0}</p>
                  <p className="text-xs text-muted-foreground">Companies</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-success/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.contacts_by_source?.email || 0}</p>
                  <p className="text-xs text-muted-foreground">From Emails</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-card border border-border">
              <TabsTrigger value="pending" className="data-[state=active]:bg-warning/20">
                <Clock className="h-4 w-4 mr-2" />
                Pending ({stats?.pending_review || 0})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="data-[state=active]:bg-primary/20">
                <Users className="h-4 w-4 mr-2" />
                Contacts
              </TabsTrigger>
              <TabsTrigger value="companies" className="data-[state=active]:bg-accent/20">
                <Building2 className="h-4 w-4 mr-2" />
                Companies
              </TabsTrigger>
              <TabsTrigger value="groups" className="data-[state=active]:bg-indigo-500/20" data-testid="groups-tab">
                <Users className="h-4 w-4 mr-2" />
                Groups
              </TabsTrigger>
              <TabsTrigger value="email-lists" className="data-[state=active]:bg-purple-500/20" data-testid="email-lists-tab">
                <Mail className="h-4 w-4 mr-2" />
                Email Lists
              </TabsTrigger>
              <TabsTrigger value="documents" className="data-[state=active]:bg-blue-500/20">
                <FileText className="h-4 w-4 mr-2" />
                Documents
              </TabsTrigger>
            </TabsList>

            {/* Pending Review Tab */}
            <TabsContent value="pending">
              <div className="space-y-4">
                {pendingContacts.length === 0 ? (
                  <div className="card-surface p-8 text-center">
                    <CheckCircle className="h-12 w-12 text-success/50 mx-auto mb-3" />
                    <p className="text-muted-foreground">No contacts pending review</p>
                  </div>
                ) : (
                  pendingContacts.map((contact) => (
                    <motion.div
                      key={contact.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="card-surface border-l-4 border-l-warning"
                    >
                      <div className="grid md:grid-cols-2 gap-4 p-4">
                        {/* Contact Info */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-foreground text-lg">{contact.name}</h3>
                            <span className="text-xs bg-warning/20 text-warning px-2 py-1 rounded">Pending</span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <p className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-foreground">{contact.email}</span>
                            </p>
                            {contact.phone && (
                              <p className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span className="text-foreground">{contact.phone}</span>
                              </p>
                            )}
                            {(contact.company_name || contact.suggested_company?.suggested_name) && (
                              <p className="flex items-center gap-2">
                                <Building className="h-4 w-4 text-muted-foreground" />
                                <span className="text-foreground">
                                  {contact.company_name || contact.suggested_company?.suggested_name}
                                  {contact.suggested_company?.existing_company && (
                                    <span className="text-success text-xs ml-2">(exists)</span>
                                  )}
                                </span>
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button size="sm" onClick={() => openConfirmDialog(contact)} data-testid={`confirm-contact-${contact.id}`}>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Confirm
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDismissContact(contact.id)}>
                              <XCircle className="h-4 w-4 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </div>

                        {/* Email Preview */}
                        <div className="bg-background rounded-lg border border-border p-3">
                          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            Source Email
                          </div>
                          {contact.source_subject && (
                            <p className="font-medium text-foreground text-sm mb-1">{contact.source_subject}</p>
                          )}
                          <p className="text-xs text-muted-foreground mb-2">
                            From: {contact.source_from} • {contact.source_date ? new Date(contact.source_date).toLocaleDateString() : ""}
                          </p>
                          <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto whitespace-pre-wrap border-t border-border pt-2">
                            {contact.source_body?.substring(0, 500)}
                            {contact.source_body?.length > 500 && "..."}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <div className="card-surface p-4">
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex-1 min-w-[250px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search contacts by name, email, or company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-contacts-input"
                    />
                  </div>
                  <Button variant="outline" onClick={() => setShowImportEmailsDialog(true)} data-testid="import-emails-btn">
                    <Download className="h-4 w-4 mr-1" /> Import Emails
                  </Button>
                  <Button onClick={() => setShowAddContactDialog(true)} data-testid="add-contact-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Contact
                  </Button>
                </div>
                
                {/* Source Filter & Sort Controls */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b flex-wrap">
                  <span className="text-sm text-muted-foreground">Source:</span>
                  <Select value={contactSourceFilter} onValueChange={(v) => { setContactSourceFilter(v); setContactsPage(1); }}>
                    <SelectTrigger className="w-[200px]" data-testid="contact-source-filter">
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="pantheon">Pantheon (Accounting)</SelectItem>
                      <SelectItem value="customer_groups">Customer Groups (Ops)</SelectItem>
                      <SelectItem value="customer_advisory">Customer Advisory</SelectItem>
                      <SelectItem value="offers_email">Offers Email</SelectItem>
                      <SelectItem value="newsletter_subscribers">Newsletter Subscribers</SelectItem>
                      <SelectItem value="hr_staff">HR / Staff Directory</SelectItem>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                      <SelectItem value="other">Other Sources</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="border-l pl-3 ml-2 flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Sort by:</span>
                    <Select value={contactSortBy} onValueChange={(v) => { setContactSortBy(v); setContactsPage(1); }}>
                      <SelectTrigger className="w-[150px]" data-testid="contact-sort-select">
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="domain">Domain</SelectItem>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="company_name">Company</SelectItem>
                        <SelectItem value="created_at">Date Added</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={contactSortOrder} onValueChange={(v) => { setContactSortOrder(v); setContactsPage(1); }}>
                      <SelectTrigger className="w-[100px]" data-testid="contact-sort-order-select">
                        <SelectValue placeholder="Order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">A → Z</SelectItem>
                        <SelectItem value="desc">Z → A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <span className="text-sm text-muted-foreground ml-auto">
                    {contactsTotal} contacts
                  </span>
                </div>
                
                {/* Directory-style Contact Cards */}
                {contacts.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Contacts Found</h3>
                    <p className="text-slate-500">Try a different search term or add a new contact</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {/* Group contacts by domain when sorting by domain, otherwise by company */}
                    {contactSortBy === "domain" ? (
                      Object.entries(
                        contacts.reduce((acc, contact) => {
                          const domain = contact.domain || (contact.email?.split("@")[1]) || "No Domain";
                          if (!acc[domain]) acc[domain] = [];
                          acc[domain].push(contact);
                          return acc;
                        }, {})
                      ).map(([domain, domainContacts]) => (
                        <div key={domain} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                          <div className="bg-blue-50 px-4 py-3 border-b flex items-center gap-2">
                            <Globe className="h-4 w-4 text-blue-600" />
                            <h3 className="font-semibold text-blue-800">{domain}</h3>
                            <span className="text-blue-600 text-sm">({domainContacts.length} contacts)</span>
                          </div>
                          <div className="divide-y">
                            {domainContacts.map((contact) => (
                              <div key={contact.id} className="p-4 hover:bg-slate-50 transition-colors group">
                                <div className="flex items-start gap-4">
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                    {contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold text-slate-900">{contact.name}</h4>
                                      {/* Company assignment */}
                                      <Popover open={contactAssignPopoverOpen === contact.id} onOpenChange={(open) => { setContactAssignPopoverOpen(open ? contact.id : null); if (!open) { setCompanySearchQuery(""); setCompanySearchResults([]); } }}>
                                        <PopoverTrigger asChild>
                                          <button className={`text-xs px-2 py-0.5 rounded ${contact.company_name ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200'}`}>
                                            <Building2 className="h-3 w-3 inline mr-1" />
                                            {contact.company_name || "Assign company"}
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64 p-3" align="start">
                                          <div className="space-y-2">
                                            <div className="font-medium text-sm">Assign to Company</div>
                                            <Input
                                              placeholder="Search companies..."
                                              value={companySearchQuery}
                                              onChange={(e) => { setCompanySearchQuery(e.target.value); searchCompaniesForAssign(e.target.value); }}
                                              className="h-8 text-sm"
                                            />
                                            {companySearchResults.length > 0 && (
                                              <div className="max-h-40 overflow-y-auto border rounded">
                                                {companySearchResults.map(comp => (
                                                  <button
                                                    key={comp.id}
                                                    onClick={() => assignContactToCompany(contact.id, comp.id, comp.name)}
                                                    className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm border-b last:border-0"
                                                  >
                                                    <div className="font-medium">{comp.name}</div>
                                                    {comp.domain && <div className="text-xs text-slate-500">{comp.domain}</div>}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <p className="text-sm text-slate-600">{contact.email}</p>
                                    {contact.phone && <p className="text-xs text-slate-500">{contact.phone}</p>}
                                    {/* Contact tags */}
                                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                                      {contact.tags?.map((tag, idx) => (
                                        <span key={idx} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                          {tag}
                                        </span>
                                      ))}
                                      <Popover open={contactTagPopoverOpen === contact.id} onOpenChange={(open) => { setContactTagPopoverOpen(open ? contact.id : null); if (!open) setNewTagInput(""); }}>
                                        <PopoverTrigger asChild>
                                          <button className="text-slate-400 hover:text-slate-600 p-0.5" data-testid={`tag-contact-${contact.id}`}>
                                            <Tag className="h-3 w-3" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-56 p-3" align="start">
                                          <div className="space-y-2">
                                            <div className="font-medium text-sm">Contact Tags</div>
                                            {contact.tags?.length > 0 && (
                                              <div className="flex flex-wrap gap-1">
                                                {contact.tags.map((tag, idx) => (
                                                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 rounded text-xs">
                                                    {tag}
                                                    <button onClick={() => removeTagFromContact(contact.id, tag)} className="hover:bg-purple-200 rounded-full" data-testid={`remove-contact-tag-${tag}`}>
                                                      <X className="h-3 w-3" />
                                                    </button>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                            <div className="flex gap-1">
                                              <Input
                                                placeholder="Type tag name..."
                                                value={newTagInput}
                                                onChange={(e) => setNewTagInput(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && newTagInput.trim() && addTagToContact(contact.id, newTagInput.trim())}
                                                className="h-7 text-xs"
                                                data-testid={`contact-tag-input-${contact.id}`}
                                              />
                                              <Button size="sm" className="h-7 px-2" onClick={() => newTagInput.trim() && addTagToContact(contact.id, newTagInput.trim())} data-testid={`add-contact-tag-btn-${contact.id}`}>
                                                <Plus className="h-3 w-3" />
                                              </Button>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                              {TAG_SUGGESTIONS.filter(t => !contact.tags?.includes(t)).slice(0, 4).map(tag => (
                                                <button key={tag} onClick={() => addTagToContact(contact.id, tag)} className="px-1.5 py-0.5 bg-slate-50 hover:bg-slate-100 rounded text-xs border">
                                                  + {tag}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingContact(contact); setShowAddContactDialog(true); }}>
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      Object.entries(
                        contacts.reduce((acc, contact) => {
                          const company = contact.company_name || "No Company";
                          if (!acc[company]) acc[company] = [];
                          acc[company].push(contact);
                          return acc;
                        }, {})
                      ).sort(([a], [b]) => a.localeCompare(b)).map(([company, companyContacts]) => (
                        <div key={company} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                          <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-[#003366]" />
                            <h3 className="font-semibold text-[#003366]">{company}</h3>
                            <span className="text-slate-500 text-sm">({companyContacts.length})</span>
                          </div>
                          <div className="divide-y">
                            {companyContacts.map((contact) => (
                              <div key={contact.id} className="p-4 hover:bg-slate-50 transition-colors group">
                                <div className="flex items-start gap-4">
                                  {/* Avatar */}
                                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#003366] to-[#0055aa] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                                    {contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                  </div>
                                  
                                  {/* Contact Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold text-slate-900">{contact.name}</h4>
                                      {contact.source === "customer_portal" && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Customer Added</span>
                                      )}
                                      {contact.source_type && contact.source !== "customer_portal" && (
                                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{contact.source_type}</span>
                                      )}
                                    </div>
                                    {contact.position && (
                                      <p className="text-sm text-slate-600">{contact.position}</p>
                                    )}
                                    
                                    {/* Contact Details */}
                                    <div className="mt-2 flex flex-wrap gap-4 text-sm">
                                      {contact.email && (
                                        <a
                                          href={`mailto:${contact.email}`}
                                          className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                                        >
                                          <Mail className="h-4 w-4" />
                                          <span className="truncate max-w-[200px]">{contact.email}</span>
                                        </a>
                                      )}
                                      {contact.phone && (
                                        <a
                                          href={`tel:${contact.phone}`}
                                          className="flex items-center gap-1.5 text-slate-600 hover:text-slate-800"
                                        >
                                          <Phone className="h-4 w-4" />
                                          <span>{contact.phone}</span>
                                        </a>
                                      )}
                                    </div>
                                    
                                    {/* Contact tags - inline editable */}
                                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                                      {contact.tags?.map((tag, idx) => (
                                        <span key={idx} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                          {tag}
                                        </span>
                                      ))}
                                      <Popover open={contactTagPopoverOpen === contact.id} onOpenChange={(open) => { setContactTagPopoverOpen(open ? contact.id : null); if (!open) setNewTagInput(""); }}>
                                        <PopoverTrigger asChild>
                                          <button className="text-slate-400 hover:text-slate-600 p-0.5" data-testid={`tag-contact-company-view-${contact.id}`}>
                                            <Tag className="h-3 w-3" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-56 p-3" align="start">
                                          <div className="space-y-2">
                                            <div className="font-medium text-sm">Contact Tags</div>
                                            {contact.tags?.length > 0 && (
                                              <div className="flex flex-wrap gap-1">
                                                {contact.tags.map((tag, idx) => (
                                                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 rounded text-xs">
                                                    {tag}
                                                    <button onClick={() => removeTagFromContact(contact.id, tag)} className="hover:bg-purple-200 rounded-full">
                                                      <X className="h-3 w-3" />
                                                    </button>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                            <div className="flex gap-1">
                                              <Input
                                                placeholder="Type tag name..."
                                                value={newTagInput}
                                                onChange={(e) => setNewTagInput(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && newTagInput.trim() && addTagToContact(contact.id, newTagInput.trim())}
                                                className="h-7 text-xs"
                                              />
                                              <Button size="sm" className="h-7 px-2" onClick={() => newTagInput.trim() && addTagToContact(contact.id, newTagInput.trim())}>
                                                <Plus className="h-3 w-3" />
                                              </Button>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                              {TAG_SUGGESTIONS.filter(t => !contact.tags?.includes(t)).slice(0, 4).map(tag => (
                                                <button key={tag} onClick={() => addTagToContact(contact.id, tag)} className="px-1.5 py-0.5 bg-slate-50 hover:bg-slate-100 rounded text-xs border">
                                                  + {tag}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleSendKYCForm(contact)}
                                      title="Send KYC Form"
                                      data-testid={`send-kyc-${contact.id}`}
                                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    >
                                      <Send className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditContactDialog(contact)}
                                      data-testid={`edit-contact-${contact.id}`}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteContact(contact.id)}
                                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-red-50"
                                      data-testid={`delete-contact-${contact.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))
                  )}
                  </div>
                )}
                
                {/* Contacts Pagination */}
                {contactsTotal > pageSize && (
                  <div className="flex items-center justify-between p-4 border-t mt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {((contactsPage - 1) * pageSize) + 1} - {Math.min(contactsPage * pageSize, contactsTotal)} of {contactsTotal} contacts
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contactsPage === 1}
                        onClick={() => setContactsPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contactsPage * pageSize >= contactsTotal}
                        onClick={() => setContactsPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Companies Tab */}
            <TabsContent value="companies">
              <div className="card-surface p-4">
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search companies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-companies-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExactMatchSearch(!exactMatchSearch)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        exactMatchSearch 
                          ? "bg-blue-100 text-blue-700 border border-blue-300" 
                          : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"
                      }`}
                      title={exactMatchSearch ? "Showing exact word matches only" : "Click for exact word matching"}
                      data-testid="exact-match-toggle"
                    >
                      <Target className="h-3.5 w-3.5" />
                      {exactMatchSearch ? "Exact Match" : "Partial Match"}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Sort by:</span>
                    <Select value={companySortBy} onValueChange={(v) => { setCompanySortBy(v); setCompaniesPage(1); }}>
                      <SelectTrigger className="w-[140px]" data-testid="company-sort-select">
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="domain">Domain</SelectItem>
                        <SelectItem value="country">Country</SelectItem>
                        <SelectItem value="industry">Industry</SelectItem>
                        <SelectItem value="created_at">Date Added</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={companySortOrder} onValueChange={(v) => { setCompanySortOrder(v); setCompaniesPage(1); }}>
                      <SelectTrigger className="w-[100px]" data-testid="company-sort-order-select">
                        <SelectValue placeholder="Order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">A → Z</SelectItem>
                        <SelectItem value="desc">Z → A</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Contacts</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((company) => (
                      <TableRow key={company.id}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell>{company.domain || "-"}</TableCell>
                        <TableCell>
                          <Popover open={tagPopoverOpen === company.id} onOpenChange={(open) => { setTagPopoverOpen(open ? company.id : null); if (!open) setNewTagInput(""); }}>
                            <PopoverTrigger asChild>
                              <button className="flex flex-wrap gap-1 items-center min-w-[80px] p-1 rounded hover:bg-slate-100 transition-colors" data-testid={`company-tags-${company.id}`}>
                                {company.tags && company.tags.length > 0 ? (
                                  <>
                                    {company.tags.map((tag, idx) => (
                                      <span 
                                        key={idx} 
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          tag.toLowerCase() === 'korea' ? 'bg-blue-100 text-blue-700' :
                                          tag.toLowerCase() === 'china' ? 'bg-red-100 text-red-700' :
                                          tag.toLowerCase() === 'usa' || tag.toLowerCase() === 'us' ? 'bg-indigo-100 text-indigo-700' :
                                          tag.toLowerCase() === 'europe' || tag.toLowerCase() === 'eu' ? 'bg-yellow-100 text-yellow-700' :
                                          tag.toLowerCase() === 'vip' ? 'bg-purple-100 text-purple-700' :
                                          tag.toLowerCase() === 'partner' ? 'bg-green-100 text-green-700' :
                                          tag.toLowerCase() === 'agent' ? 'bg-orange-100 text-orange-700' :
                                          'bg-slate-100 text-slate-700'
                                        }`}
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                    <Tag className="h-3 w-3 text-slate-400" />
                                  </>
                                ) : (
                                  <span className="text-slate-400 flex items-center gap-1 text-sm">
                                    <Tag className="h-3 w-3" /> Add tag
                                  </span>
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-3" align="start">
                              <div className="space-y-3">
                                <div className="font-medium text-sm">Manage Tags</div>
                                {/* Current tags with remove */}
                                {company.tags && company.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {company.tags.map((tag, idx) => (
                                      <span 
                                        key={idx} 
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-xs"
                                      >
                                        {tag}
                                        <button 
                                          onClick={() => removeTagFromCompany(company.id, tag)}
                                          className="hover:bg-slate-200 rounded-full p-0.5"
                                          data-testid={`remove-company-tag-${company.id}-${tag}`}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {/* Add new tag */}
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Type tag name..."
                                    value={newTagInput}
                                    onChange={(e) => setNewTagInput(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && newTagInput.trim()) {
                                        addTagToCompany(company.id, newTagInput.trim());
                                      }
                                    }}
                                    className="h-8 text-sm"
                                    data-testid={`company-tag-input-${company.id}`}
                                  />
                                  <Button 
                                    size="sm" 
                                    className="h-8"
                                    onClick={() => newTagInput.trim() && addTagToCompany(company.id, newTagInput.trim())}
                                    data-testid={`add-company-tag-btn-${company.id}`}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                {/* Quick suggestions */}
                                <div className="flex flex-wrap gap-1">
                                  {TAG_SUGGESTIONS.filter(t => !company.tags?.includes(t)).slice(0, 6).map(tag => (
                                    <button
                                      key={tag}
                                      onClick={() => addTagToCompany(company.id, tag)}
                                      className="px-2 py-0.5 bg-slate-50 hover:bg-slate-100 rounded text-xs border"
                                    >
                                      + {tag}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell>{company.country || "-"}</TableCell>
                        <TableCell>{company.industry || "-"}</TableCell>
                        <TableCell>{company.contact_count || 0}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => generateCreditAgreement(company)}
                              disabled={generatingAgreement === `credit-${company.id}`}
                              title="Generate Credit Agreement"
                              data-testid={`credit-agreement-${company.id}`}
                            >
                              <CreditCard className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => generateAgencyAgreement(company)}
                              disabled={generatingAgreement === `agency-${company.id}`}
                              title="Generate Agency Agreement"
                              data-testid={`agency-agreement-${company.id}`}
                            >
                              <Handshake className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditCompanyDialog(company)}
                              data-testid={`edit-company-${company.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteCompany(company.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-company-${company.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {companies.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No companies found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {/* Companies Pagination */}
                {companiesTotal > pageSize && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {((companiesPage - 1) * pageSize) + 1} - {Math.min(companiesPage * pageSize, companiesTotal)} of {companiesTotal} companies
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={companiesPage === 1}
                        onClick={() => setCompaniesPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={companiesPage * pageSize >= companiesTotal}
                        onClick={() => setCompaniesPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Groups Tab - Company Groups */}
            <TabsContent value="groups">
              <div className="card-surface p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <Users className="h-5 w-5 text-indigo-600" />
                      Company Groups
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Group related Pantheon companies (e.g., same company with multiple offices)
                    </p>
                  </div>
                  <Button onClick={() => setShowCreateGroupDialog(true)} data-testid="create-group-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Groups List */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl border shadow-sm">
                      <div className="p-4 border-b">
                        <h3 className="font-semibold text-slate-800">Groups ({groups.length})</h3>
                      </div>
                      
                      {loadingGroups ? (
                        <div className="p-8 text-center">
                          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
                        </div>
                      ) : groups.length === 0 ? (
                        <div className="p-8 text-center text-slate-400">
                          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No groups yet</p>
                          <p className="text-sm mt-1">Create a group to organize companies</p>
                        </div>
                      ) : (
                        <div className="divide-y max-h-[500px] overflow-y-auto">
                          {groups.map((group) => (
                            <button
                              key={group.id}
                              onClick={() => fetchGroupDetails(group.id)}
                              className={`w-full p-4 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors ${
                                selectedGroup?.id === group.id ? "bg-indigo-50" : ""
                              }`}
                              data-testid={`group-${group.id}`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                selectedGroup?.id === group.id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
                              }`}>
                                <Users className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-slate-800 truncate">{group.name}</h4>
                                <p className="text-sm text-slate-500">{group.member_count || 0} companies</p>
                              </div>
                              <ChevronRight className={`h-5 w-5 ${
                                selectedGroup?.id === group.id ? "text-indigo-600" : "text-slate-400"
                              }`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Group Details */}
                  <div className="lg:col-span-2">
                    {selectedGroup ? (
                      <div className="bg-white rounded-xl border shadow-sm">
                        {/* Group Header */}
                        <div className="p-6 border-b">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-xl font-bold text-slate-800">{selectedGroup.name}</h3>
                              {selectedGroup.description && (
                                <p className="text-slate-500 mt-1">{selectedGroup.description}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditGroupDialog(selectedGroup)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleDeleteGroup(selectedGroup.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Members Section */}
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-slate-800">
                              Member Companies ({selectedGroup.member_companies?.length || 0})
                            </h4>
                            <Button
                              size="sm"
                              onClick={() => setShowAddMemberDialog(true)}
                              data-testid="add-member-btn"
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Add Company
                            </Button>
                          </div>

                          {!selectedGroup.member_companies?.length ? (
                            <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-xl">
                              <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p>No companies in this group yet</p>
                              <Button
                                variant="link"
                                onClick={() => setShowAddMemberDialog(true)}
                                className="mt-2"
                              >
                                Add your first company
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {selectedGroup.member_companies.map((member) => (
                                <div
                                  key={member.company_id}
                                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                                >
                                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                    <Building2 className="h-5 w-5 text-indigo-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium text-slate-800">{member.company_name}</h5>
                                    <p className="text-sm text-slate-500">
                                      {member.country || "No country"} 
                                      {member.accounting_ref && ` • Pantheon: ${member.accounting_ref}`}
                                    </p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleRemoveMember(member.company_id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
                        <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-600">Select a Group</h3>
                        <p className="text-slate-400 mt-1">Choose a group from the list to view and manage its members</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Documents Tab - Form Templates by Category */}
            <TabsContent value="documents">
              <div className="space-y-4">
                {/* Upload Section at Top */}
                <div className="card-surface p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-indigo-500/10 rounded-lg flex items-center justify-center">
                        <Upload className="h-6 w-6 text-indigo-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Upload Form Templates</h3>
                        <p className="text-sm text-muted-foreground">Upload blank forms and tag them to categories (folders)</p>
                      </div>
                    </div>
                    <Button onClick={() => setShowUploadDialog(true)} className="gap-2">
                      <Upload className="h-4 w-4" />
                      Upload Form
                    </Button>
                  </div>
                </div>

                {/* Document Folders - Each category is a folder */}
                {/* Company Documents per Vertical — official company documents
                    (registry, VAT, bank details, certs) per business unit.
                    Customers can request these via WhatsApp. */}
                <div className="card-surface p-6" data-testid="company-docs-vertical-section">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          Company Documents
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">by vertical</Badge>
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Official documents per business unit — company registry, bank details, VAT, EORI, ISO, etc.
                          Accessible to customers via WhatsApp queries.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { key: "europacific", label: "Europacific", emoji: "🚢", color: "blue" },
                      { key: "cargo_cfs", label: "Cargo CFS", emoji: "📦", color: "green" },
                      { key: "ep_holding", label: "EP Holding", emoji: "🏛️", color: "purple" },
                      { key: "ep_as", label: "EP A&S", emoji: "⚓", color: "indigo" },
                    ].map((v) => {
                      const verticalDocs = uploadedDocs.filter(d => d.category === "company_docs" && d.vertical === v.key);
                      const dragOn = companyDocDragActive === v.key;
                      const isUploading = companyDocUploading === v.key;
                      const tagDraft = companyDocTagDraft[v.key] || "company_registry";
                      // ── Freshness helpers ───────────────────────────────
                      // Critical tags (customer-facing, money-related, legal)
                      // get a stricter 6-month staleness window. Other tags
                      // (insurance, ISO) use 12 months.
                      const CRITICAL_TAGS = ["bank_details", "vat_cert", "company_registry", "eori"];
                      const ageDaysOf = (iso) => {
                        if (!iso) return Infinity;
                        const t = new Date(iso).getTime();
                        if (isNaN(t)) return Infinity;
                        return Math.floor((Date.now() - t) / 86400000);
                      };
                      const formatAge = (days) => {
                        if (days < 1) return "today";
                        if (days < 2) return "yesterday";
                        if (days < 30) return `${days}d ago`;
                        if (days < 365) return `${Math.round(days / 30)}mo ago`;
                        return `${Math.floor(days / 365)}y ${Math.round((days % 365) / 30)}mo ago`;
                      };
                      const isStale = (doc) => {
                        const d = ageDaysOf(doc.created_at);
                        const limit = CRITICAL_TAGS.includes(doc.doc_tag) ? 180 : 365;
                        return d > limit;
                      };
                      const newest = verticalDocs.length
                        ? verticalDocs.reduce((m, d) => (ageDaysOf(d.created_at) < ageDaysOf(m.created_at) ? d : m))
                        : null;
                      const newestAge = newest ? ageDaysOf(newest.created_at) : null;
                      const staleCount = verticalDocs.filter(isStale).length;
                      return (
                        <div
                          key={v.key}
                          className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                          data-testid={`vertical-card-${v.key}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{v.emoji}</span>
                              <div>
                                <h4 className="font-semibold flex items-center gap-1.5">
                                  {v.label}
                                  {staleCount > 0 && (
                                    <span
                                      className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300"
                                      title={`${staleCount} document${staleCount === 1 ? "" : "s"} may be stale — review the highlighted rows below`}
                                      data-testid={`vertical-stale-badge-${v.key}`}
                                    >
                                      ⚠️ {staleCount} stale
                                    </span>
                                  )}
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  {verticalDocs.length} document{verticalDocs.length === 1 ? "" : "s"}
                                  {newestAge !== null && (
                                    <span className="ml-1">• last updated {formatAge(newestAge)}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {/* Tag dropdown — the doc_tag to apply when a file is dropped/picked. */}
                            <div className="w-56">
                              <Select
                                value={tagDraft}
                                onValueChange={(value) => setCompanyDocTagDraft(prev => ({ ...prev, [v.key]: value }))}
                              >
                                <SelectTrigger className="h-8 text-xs" data-testid={`vertical-tag-${v.key}`}>
                                  <SelectValue placeholder="Select doc type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="company_registry">Company Registry</SelectItem>
                                  <SelectItem value="bank_details">Bank Details</SelectItem>
                                  <SelectItem value="vat_cert">VAT Certificate</SelectItem>
                                  <SelectItem value="eori">EORI Certificate</SelectItem>
                                  <SelectItem value="aoeo_cert">AEO Certificate</SelectItem>
                                  <SelectItem value="iso_cert">ISO Certificate</SelectItem>
                                  <SelectItem value="insurance">Insurance</SelectItem>
                                  <SelectItem value="tax_residency">Tax Residency</SelectItem>
                                  <SelectItem value="license">License</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Inline drag-drop zone (file picker via click).
                              Matches the PF Documents UX the user explicitly asked for. */}
                          <label
                            htmlFor={`vertical-upload-${v.key}`}
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setCompanyDocDragActive(v.key); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDragLeave={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              if (!e.currentTarget.contains(e.relatedTarget)) setCompanyDocDragActive(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault(); e.stopPropagation();
                              setCompanyDocDragActive(null);
                              const file = e.dataTransfer?.files?.[0];
                              if (file) handleCompanyDocUpload(v.key, file, tagDraft);
                            }}
                            className={`block cursor-pointer text-center py-6 border-2 border-dashed rounded transition-colors ${
                              dragOn ? "border-amber-500 bg-amber-50" : "border-border hover:bg-accent/5"
                            } ${isUploading ? "opacity-60 pointer-events-none" : ""}`}
                            data-testid={`vertical-drop-${v.key}`}
                          >
                            <input
                              id={`vertical-upload-${v.key}`}
                              type="file"
                              className="hidden"
                              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleCompanyDocUpload(v.key, file, tagDraft);
                                e.target.value = ""; // allow re-upload same file later
                              }}
                            />
                            {isUploading ? (
                              <>
                                <Loader2 className="h-6 w-6 mx-auto mb-1 animate-spin text-amber-500" />
                                <p className="text-xs text-muted-foreground">Uploading…</p>
                              </>
                            ) : (
                              <>
                                <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                                <p className="text-xs font-medium">Drag & drop documents here</p>
                                <p className="text-[11px] text-muted-foreground">or click to select files</p>
                              </>
                            )}
                          </label>

                          {/* Existing docs list */}
                          {verticalDocs.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {verticalDocs.map((doc) => {
                                const docDays = ageDaysOf(doc.created_at);
                                const stale = isStale(doc);
                                return (
                                <div
                                  key={doc.id}
                                  className={`flex items-center justify-between p-2 border rounded hover:bg-accent/5 group ${
                                    stale ? "border-amber-300 bg-amber-50/40" : "border-border"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FileText className={`h-4 w-4 text-${v.color}-500 flex-shrink-0`} />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium truncate flex items-center gap-1.5" title={doc.name}>
                                        {doc.name}
                                        {stale && (
                                          <span
                                            className="text-[10px] px-1 py-0 rounded bg-amber-100 text-amber-700 border border-amber-300 font-medium"
                                            title="This document hasn't been refreshed in a while — verify it's still accurate before sharing"
                                            data-testid={`doc-stale-${doc.id}`}
                                          >
                                            ⚠️ stale
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-[11px] text-muted-foreground">
                                        {doc.doc_tag ? doc.doc_tag.replace(/_/g, " ") : "untagged"} • {formatAge(docDays)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(
                                            `${API_URL}/crm/uploaded-documents/${doc.id}/download`,
                                            { headers: getAuthHeaders(), responseType: 'blob' }
                                          );
                                          const url = window.URL.createObjectURL(new Blob([response.data]));
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.setAttribute('download', doc.original_filename || doc.name || 'document');
                                          document.body.appendChild(link);
                                          link.click();
                                          link.remove();
                                          window.URL.revokeObjectURL(url);
                                        } catch (error) {
                                          toast.error("Failed to download document");
                                        }
                                      }}
                                      className="p-1 hover:bg-accent rounded"
                                      title="Download"
                                      data-testid={`download-doc-${doc.id}`}
                                    >
                                      <Download className="h-4 w-4 text-blue-500" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteDocument(doc.id)}
                                      className="p-1 hover:bg-red-50 rounded"
                                      title="Delete"
                                      data-testid={`delete-doc-${doc.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </button>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Document Folders - Each category is a folder */}
                {[
                  { key: "customs", label: "Customs Forms", desc: "Customs procedures, 4200, disposition forms", color: "orange", icon: FileText },
                  { key: "finance", label: "Finance Forms", desc: "Invoice templates, credit agreements, payment forms", color: "green", icon: CreditCard },
                  { key: "crm", label: "CRM Forms", desc: "Service agreements, KYC forms, customer documents", color: "blue", icon: Users },
                  { key: "hr", label: "HR Forms", desc: "Employee forms, leave requests, contracts", color: "purple", icon: Users },
                  { key: "operations", label: "Operations Forms", desc: "Transport orders, booking forms, manifests", color: "teal", icon: FileText },
                  { key: "general", label: "General Forms", desc: "Other miscellaneous form templates", color: "gray", icon: Folder },
                ].map((folder) => {
                  const searchTerm = (folderSearch[folder.key] || "").toLowerCase();
                  const allFolderDocs = uploadedDocs.filter(d => d.category === folder.key);
                  const folderDocs = searchTerm 
                    ? allFolderDocs.filter(d => d.name.toLowerCase().includes(searchTerm))
                    : allFolderDocs;
                  const FolderIcon = folder.icon;
                  const isDragOver = folderDragActive === folder.key;
                  return (
                    <div 
                      key={folder.key} 
                      className={`card-surface p-6 transition-all duration-200 relative ${isDragOver ? 'ring-2 ring-indigo-500 bg-indigo-50/50' : ''}`}
                      onDragEnter={(e) => handleFolderDragEnter(e, folder.key)}
                      onDragLeave={handleFolderDragLeave}
                      onDragOver={handleFolderDragOver}
                      onDrop={(e) => handleFolderDrop(e, folder.key)}
                    >
                      {/* Drag overlay */}
                      {isDragOver && (
                        <div className="absolute inset-0 flex items-center justify-center bg-indigo-500/10 rounded-lg border-2 border-dashed border-indigo-500 z-10 pointer-events-none">
                          <div className="text-center">
                            <Upload className="h-10 w-10 mx-auto text-indigo-500 mb-2" />
                            <p className="font-medium text-indigo-600">Drop to add to {folder.label}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 bg-${folder.color}-500/10 rounded-lg flex items-center justify-center`}>
                            <FolderIcon className={`h-6 w-6 text-${folder.color}-500`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              {folder.label}
                              <Badge variant="secondary" className="text-xs">{allFolderDocs.length}</Badge>
                            </h3>
                            <p className="text-sm text-muted-foreground">{folder.desc}</p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setUploadForm(prev => ({ ...prev, category: folder.key }));
                            setShowUploadDialog(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>

                      {/* Fillable official customs forms — type on screen, print & sign */}
                      {folder.key === "customs" && (
                        <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50/60 p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <FileText className="h-4 w-4 text-amber-600" />
                            <h4 className="text-sm font-semibold text-amber-800">Fillable official forms</h4>
                          </div>
                          <p className="text-xs text-amber-700/80 mb-3">
                            Open a form, type into the blanks on screen, then print &amp; sign. Or copy the link to send to the customer.
                          </p>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {[
                              { slug: "auth4200", title: "Authorisation — Procedure 42" },
                              { slug: "statement-koper", title: "Statement — Delivery from Koper" },
                              { slug: "poa-fiscal", title: "Power of Attorney — Fiscal Rep." },
                              { slug: "disposition", title: "Disposition for Import" },
                              { slug: "ddv-poo42", title: "Authorisation 42/63 (DDV / VAT)" },
                            ].map((cf) => (
                              <div
                                key={cf.slug}
                                className="flex items-center justify-between gap-2 bg-white rounded-md border border-amber-200 px-3 py-2"
                              >
                                <span className="text-sm font-medium text-slate-700 truncate">{cf.title}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button
                                    size="sm" variant="outline" className="h-7 px-2 text-xs"
                                    onClick={() => window.open(`/customs-form/${cf.slug}`, "_blank")}
                                    data-testid={`fill-print-${cf.slug}`}
                                  >
                                    <FileText className="h-3.5 w-3.5 mr-1" /> Fill &amp; Print
                                  </Button>
                                  <button
                                    title="Copy customer link"
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}/customs-form/${cf.slug}`);
                                      toast.success("Customer link copied");
                                    }}
                                    className="p-1.5 hover:bg-amber-100 rounded"
                                    data-testid={`copy-link-${cf.slug}`}
                                  >
                                    <Link2 className="h-4 w-4 text-amber-600" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Search bar for folder */}
                      {allFolderDocs.length > 0 && (
                        <div className="mb-4">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder={`Search ${folder.label.toLowerCase()}...`}
                              value={folderSearch[folder.key] || ""}
                              onChange={(e) => setFolderSearch(prev => ({ ...prev, [folder.key]: e.target.value }))}
                              className="pl-9 h-9"
                            />
                            {folderSearch[folder.key] && (
                              <button
                                onClick={() => setFolderSearch(prev => ({ ...prev, [folder.key]: "" }))}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          {searchTerm && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Showing {folderDocs.length} of {allFolderDocs.length} documents
                            </p>
                          )}
                        </div>
                      )}
                      
                      {allFolderDocs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-border rounded-lg hover:border-indigo-300 transition-colors">
                          <Upload className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p className="font-medium">Drop files here</p>
                          <p className="text-sm">or click "Add" to upload forms</p>
                        </div>
                      ) : folderDocs.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground border border-dashed border-border rounded-lg">
                          <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No documents match "{searchTerm}"</p>
                        </div>
                      ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {folderDocs.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/5 group">
                              <div className="flex items-center gap-3 min-w-0">
                                <FileText className={`h-5 w-5 text-${folder.color}-500 flex-shrink-0`} />
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate">{doc.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(doc.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await axios.get(
                                        `${API_URL}/crm/uploaded-documents/${doc.id}/download`,
                                        {
                                          headers: getAuthHeaders(),
                                          responseType: 'blob'
                                        }
                                      );
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      // Use original_filename if available, fall back to name
                                      link.setAttribute('download', doc.original_filename || doc.name || 'document');
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      console.error("Download error:", error);
                                      toast.error("Failed to download document");
                                    }
                                  }}
                                  className="p-1.5 hover:bg-accent rounded"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4 text-blue-500" />
                                </button>
                                <button
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="p-1.5 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Email Lists Tab - Consolidated view of all email sources */}
            <TabsContent value="email-lists">
              <div className="card-surface p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <Mail className="h-5 w-5 text-purple-600" />
                      Email Lists Overview
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Consolidated view of all email sources in one place. Import emails from any source into CRM contacts.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchEmailSourcesStats} disabled={loadingEmailStats} data-testid="refresh-email-stats">
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingEmailStats ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button onClick={() => setShowImportEmailsDialog(true)} data-testid="import-emails-from-tab">
                      <Download className="h-4 w-4 mr-2" />
                      Import Emails
                    </Button>
                  </div>
                </div>

                {/* Total Unique Emails Stats */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6 border border-purple-200">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-700">{emailSourcesStats?.total_unique_emails || 0}</p>
                      <p className="text-sm text-purple-600">Total Unique Contacts in CRM</p>
                    </div>
                  </div>
                </div>

                {/* Email Sources Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Newsletter Subscribers */}
                  <div className="bg-white rounded-xl border border-purple-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Newsletter Subscribers</h3>
                        <p className="text-xs text-slate-500">Active newsletter list</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-purple-600">{emailSourcesStats?.sources?.newsletter_subscribers?.available || 0}</p>
                        <p className="text-xs text-slate-500">available</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.newsletter_subscribers?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setEmailImportSource("newsletter_subscribers"); setShowImportEmailsDialog(true); }}
                      data-testid="import-newsletter-btn"
                    >
                      Import to CRM
                    </Button>
                  </div>

                  {/* HR / Staff Directory */}
                  <div className="bg-white rounded-xl border border-green-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">HR / Staff Directory</h3>
                        <p className="text-xs text-slate-500">Internal staff emails</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-green-600">{emailSourcesStats?.sources?.hr_staff?.available || 0}</p>
                        <p className="text-xs text-slate-500">available</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.hr_staff?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setEmailImportSource("hr_staff"); setShowImportEmailsDialog(true); }}
                      data-testid="import-hr-btn"
                    >
                      Import to CRM
                    </Button>
                  </div>

                  {/* Customer Advisory */}
                  <div className="bg-white rounded-xl border border-blue-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Target className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Customer Advisory</h3>
                        <p className="text-xs text-slate-500">From offers@europacific.com</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{emailSourcesStats?.sources?.customer_advisory?.available || 0}</p>
                        <p className="text-xs text-slate-500">available</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.customer_advisory?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setEmailImportSource("customer_advisory"); setShowImportEmailsDialog(true); }}
                      data-testid="import-advisory-btn"
                    >
                      Import to CRM
                    </Button>
                  </div>

                  {/* Customer Groups */}
                  <div className="bg-white rounded-xl border border-orange-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <Folder className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Customer Groups</h3>
                        <p className="text-xs text-slate-500">Operations groups</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-orange-600">-</p>
                        <p className="text-xs text-slate-500">varies</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.customer_groups?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setEmailImportSource("customer_groups"); setShowImportEmailsDialog(true); }}
                      data-testid="import-groups-btn"
                    >
                      Import to CRM
                    </Button>
                  </div>

                  {/* Offers/Quotes Emails */}
                  <div className="bg-white rounded-xl border border-cyan-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Offers Emails</h3>
                        <p className="text-xs text-slate-500">From quotes/offers</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-cyan-600">{emailSourcesStats?.sources?.offers_email?.available || 0}</p>
                        <p className="text-xs text-slate-500">available</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.offers_email?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setEmailImportSource("offers_email"); setShowImportEmailsDialog(true); }}
                      data-testid="import-offers-btn"
                    >
                      Import to CRM
                    </Button>
                  </div>

                  {/* Pantheon (Accounting) */}
                  <div className="bg-white rounded-xl border border-amber-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Pantheon (Accounting)</h3>
                        <p className="text-xs text-slate-500">From accounting system</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold text-amber-600">-</p>
                        <p className="text-xs text-slate-500">via import</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-green-600">{emailSourcesStats?.sources?.pantheon?.imported || 0}</p>
                        <p className="text-xs text-slate-500">imported to CRM</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3" 
                      onClick={() => { setShowPantheonDialog(true); fetchPantheonStats(); }}
                      data-testid="import-pantheon-btn"
                    >
                      Import Excel
                    </Button>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">About Email Lists</h4>
                  <p className="text-sm text-blue-700">
                    This page consolidates all email sources from across the application. Import contacts from any source 
                    into the CRM to have them all searchable in one place. Use the "Source" filter in the Contacts tab 
                    to view contacts by their original source.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Confirm Contact Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Contact</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={confirmForm.first_name}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={confirmForm.last_name}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={confirmForm.email}
                  onChange={(e) => setConfirmForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={confirmForm.phone}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={confirmForm.mobile}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, mobile: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>WhatsApp</Label>
                  <Input
                    placeholder="+386..."
                    value={confirmForm.whatsapp}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Viber</Label>
                  <Input
                    placeholder="+386..."
                    value={confirmForm.viber}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, viber: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  value={confirmForm.position}
                  onChange={(e) => setConfirmForm(prev => ({ ...prev, position: e.target.value }))}
                />
              </div>
              <div>
                <Label>Birthday</Label>
                <Input
                  type="date"
                  value={confirmForm.birthday}
                  onChange={(e) => setConfirmForm(prev => ({ ...prev, birthday: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Company</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Search or enter company name..."
                    value={confirmForm.company_name}
                    onChange={(e) => {
                      setConfirmForm(prev => ({ ...prev, company_name: e.target.value, company_id: "" }));
                      searchCompanies(e.target.value);
                    }}
                  />
                  {companySearch.length > 0 && (
                    <div className="border rounded-md max-h-32 overflow-y-auto">
                      {companySearch.map(c => (
                        <button
                          key={c.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                          onClick={() => {
                            setConfirmForm(prev => ({ ...prev, company_id: c.id, company_name: c.name }));
                            setCompanySearch([]);
                          }}
                        >
                          {c.name} <span className="text-muted-foreground">({c.domain})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="create_company"
                      checked={confirmForm.create_company}
                      onChange={(e) => setConfirmForm(prev => ({ ...prev, create_company: e.target.checked }))}
                    />
                    <label htmlFor="create_company" className="text-sm text-muted-foreground">
                      Create new company if not found
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input
                  placeholder="https://linkedin.com/in/..."
                  value={confirmForm.linkedin}
                  onChange={(e) => setConfirmForm(prev => ({ ...prev, linkedin: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Facebook</Label>
                  <Input
                    placeholder="https://facebook.com/..."
                    value={confirmForm.facebook}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, facebook: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input
                    placeholder="@username"
                    value={confirmForm.instagram}
                    onChange={(e) => setConfirmForm(prev => ({ ...prev, instagram: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={confirmForm.notes}
                  onChange={(e) => setConfirmForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmContact} data-testid="save-contact-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={showAddContactDialog} onOpenChange={setShowAddContactDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Contact</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={newContact.first_name}
                    onChange={(e) => setNewContact(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={newContact.last_name}
                    onChange={(e) => setNewContact(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newContact.phone}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={newContact.mobile}
                    onChange={(e) => setNewContact(prev => ({ ...prev, mobile: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>WhatsApp</Label>
                  <Input
                    placeholder="+386..."
                    value={newContact.whatsapp}
                    onChange={(e) => setNewContact(prev => ({ ...prev, whatsapp: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Viber</Label>
                  <Input
                    placeholder="+386..."
                    value={newContact.viber}
                    onChange={(e) => setNewContact(prev => ({ ...prev, viber: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  value={newContact.position}
                  onChange={(e) => setNewContact(prev => ({ ...prev, position: e.target.value }))}
                />
              </div>
              <div>
                <Label>Birthday</Label>
                <Input
                  type="date"
                  value={newContact.birthday}
                  onChange={(e) => setNewContact(prev => ({ ...prev, birthday: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>Company</Label>
                <Input
                  placeholder="Company name..."
                  value={newContact.company_name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input
                  placeholder="https://linkedin.com/in/..."
                  value={newContact.linkedin}
                  onChange={(e) => setNewContact(prev => ({ ...prev, linkedin: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Facebook</Label>
                  <Input
                    placeholder="https://facebook.com/..."
                    value={newContact.facebook}
                    onChange={(e) => setNewContact(prev => ({ ...prev, facebook: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input
                    placeholder="@username"
                    value={newContact.instagram}
                    onChange={(e) => setNewContact(prev => ({ ...prev, instagram: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newContact.notes}
                  onChange={(e) => setNewContact(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              
              {/* KYC Section */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <h4 className="font-medium text-blue-800">Send KYC Form</h4>
                </div>
                <p className="text-xs text-blue-600 mb-3">
                  Send a KYC form to this contact to collect company details, VAT, and compliance information.
                </p>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  className="border-blue-500 text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    if (newContact.email) {
                      setKycForm({
                        company_name: newContact.company_name || "",
                        contact_email: newContact.email,
                        contact_name: `${newContact.first_name} ${newContact.last_name}`.trim() || "",
                        notes: ""
                      });
                      setShowKYCDialog(true);
                    } else {
                      toast.error("Enter email first to send KYC form");
                    }
                  }}
                  data-testid="send-kyc-from-add-contact-btn"
                >
                  <Send className="h-3 w-3 mr-1" />
                  Send KYC Form to This Contact
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddContactDialog(false)}>Cancel</Button>
            <Button onClick={handleAddContact} disabled={!newContact.email} data-testid="create-contact-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Create Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={newCompany.name}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Domain</Label>
                <Input
                  placeholder="example.com"
                  value={newCompany.domain}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, domain: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={newCompany.address}
                onChange={(e) => setNewCompany(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={newCompany.city}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={newCompany.country}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, country: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>VAT Number</Label>
                <Input
                  value={newCompany.vat_number}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, vat_number: e.target.value }))}
                />
              </div>
              <div>
                <Label>Pantheon Code</Label>
                <Input
                  placeholder="e.g. 100234"
                  value={newCompany.pantheon_code}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, pantheon_code: e.target.value }))}
                  data-testid="pantheon-code-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newCompany.phone}
                  onChange={(e) => setNewCompany(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label>Industry</Label>
                <Select value={newCompany.industry} onValueChange={(v) => setNewCompany(prev => ({ ...prev, industry: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shipper">Shipper</SelectItem>
                    <SelectItem value="carrier">Carrier</SelectItem>
                    <SelectItem value="freight_forwarder">Freight Forwarder</SelectItem>
                    <SelectItem value="customs_broker">Customs Broker</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCompanyDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCompany} disabled={!newCompany.name} data-testid="save-company-btn">
              <Building2 className="h-4 w-4 mr-2" />
              Create Company
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={showEditContactDialog} onOpenChange={setShowEditContactDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          {editingContact && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={editingContact.first_name}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, first_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={editingContact.last_name}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, last_name: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editingContact.email}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editingContact.phone}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <Input
                      value={editingContact.mobile}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, mobile: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>WhatsApp</Label>
                    <Input
                      placeholder="+386..."
                      value={editingContact.whatsapp}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, whatsapp: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Viber</Label>
                    <Input
                      placeholder="+386..."
                      value={editingContact.viber}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, viber: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label>Position</Label>
                  <Input
                    value={editingContact.position}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, position: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Birthday</Label>
                  <Input
                    type="date"
                    value={editingContact.birthday}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, birthday: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Company</Label>
                  <Input
                    placeholder="Company name..."
                    value={editingContact.company_name}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, company_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>LinkedIn</Label>
                  <Input
                    placeholder="https://linkedin.com/in/..."
                    value={editingContact.linkedin}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, linkedin: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      placeholder="https://facebook.com/..."
                      value={editingContact.facebook}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, facebook: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      placeholder="@username"
                      value={editingContact.instagram}
                      onChange={(e) => setEditingContact(prev => ({ ...prev, instagram: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editingContact.notes}
                    onChange={(e) => setEditingContact(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditContactDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateContact} data-testid="update-contact-btn">
              <Edit className="h-4 w-4 mr-2" />
              Update Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEditCompanyDialog} onOpenChange={setShowEditCompanyDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Edit Company
            </DialogTitle>
          </DialogHeader>
          {editingCompany && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Company Name *</Label>
                  <Input
                    value={editingCompany.name || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Domain</Label>
                  <Input
                    placeholder="example.com"
                    value={editingCompany.domain || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, domain: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Accounting & VAT */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Accounting Reference (Pantheon)</Label>
                  <Input
                    value={editingCompany.accounting_ref || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, accounting_ref: e.target.value }))}
                    placeholder="e.g., 12345"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">VAT Number</Label>
                  <Input
                    value={editingCompany.vat_number || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="e.g., SI12345678"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <Label className="text-xs text-slate-500">Address</Label>
                <Input
                  value={editingCompany.address || ""}
                  onChange={(e) => setEditingCompany(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">City</Label>
                  <Input
                    value={editingCompany.city || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, city: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Postal Code</Label>
                  <Input
                    value={editingCompany.postal_code || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, postal_code: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Input
                    value={editingCompany.country || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, country: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input
                    type="email"
                    value={editingCompany.email || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="info@company.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <Input
                    value={editingCompany.phone || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+386 1 234 5678"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Website</Label>
                  <Input
                    value={editingCompany.website || ""}
                    onChange={(e) => setEditingCompany(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://www.company.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Industry</Label>
                  <Select 
                    value={editingCompany.industry || "none"} 
                    onValueChange={(v) => setEditingCompany(prev => ({ ...prev, industry: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      <SelectItem value="shipper">Shipper</SelectItem>
                      <SelectItem value="carrier">Carrier</SelectItem>
                      <SelectItem value="freight_forwarder">Freight Forwarder</SelectItem>
                      <SelectItem value="customs_broker">Customs Broker</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                      <SelectItem value="terminal">Terminal</SelectItem>
                      <SelectItem value="shipping_line">Shipping Line</SelectItem>
                      <SelectItem value="trucking">Trucking</SelectItem>
                      <SelectItem value="rail">Rail</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-xs text-slate-500">Notes</Label>
                <Textarea
                  value={editingCompany.notes || ""}
                  onChange={(e) => setEditingCompany(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes about this company..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              {/* Pantheon Sync Status */}
              <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Pantheon Synced</Label>
                  {editingCompany.pantheon_synced ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Yes</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Pending</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingCompany.pantheon_synced || false}
                    onCheckedChange={(checked) => setEditingCompany(prev => ({ ...prev, pantheon_synced: checked }))}
                  />
                  <span className="text-xs text-slate-500">Mark as synced</span>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditCompanyDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateCompany} disabled={!editingCompany?.name} data-testid="update-company-btn">
              <Check className="h-4 w-4 mr-2" />
              Update Company
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* KYC Link Dialog */}
      <Dialog open={showKYCDialog} onOpenChange={resetKycDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Send KYC Registration Form
            </DialogTitle>
          </DialogHeader>
          
          {!kycLink ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Send a company registration form to a new or potential customer. They can fill in company details, banking info, and payment contacts.
              </p>
              
              <div>
                <Label>Company Name (optional)</Label>
                <Input
                  value={kycForm.company_name}
                  onChange={(e) => setKycForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Customer company name"
                />
              </div>
              
              <div>
                <Label>Contact Name (optional)</Label>
                <Input
                  value={kycForm.contact_name}
                  onChange={(e) => setKycForm(prev => ({ ...prev, contact_name: e.target.value }))}
                  placeholder="Contact person name"
                />
              </div>
              
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={kycForm.contact_email}
                  onChange={(e) => setKycForm(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="email@company.com"
                  required
                />
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={kycForm.notes}
                  onChange={(e) => setKycForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes..."
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={resetKycDialog}>Cancel</Button>
                <Button onClick={handleSendKYC} disabled={kycSending || !kycForm.contact_email}>
                  {kycSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send KYC Link
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-800">KYC Link Created!</p>
                <p className="text-sm text-green-700">Reference: {kycLink.company_ref}</p>
              </div>
              
              <div>
                <Label className="text-sm text-muted-foreground">Share this link with the customer:</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={kycLink.kyc_link} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copyKycLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {kycLink.email_sent ? (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Email sent to {kycLink.contact_email}
                </p>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  Email not sent - please share the link manually
                </p>
              )}
              
              <div className="flex justify-end">
                <Button onClick={resetKycDialog}>Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Drag and Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? "border-indigo-500 bg-indigo-50" : "border-border hover:border-indigo-300"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="h-12 w-12 mx-auto text-indigo-500" />
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setSelectedFile(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="font-medium">Drag and drop your file here</p>
                  <p className="text-sm text-muted-foreground mb-3">or click to browse</p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Button variant="outline" onClick={() => document.getElementById("file-upload").click()}>
                    Browse Files
                  </Button>
                </div>
              )}
            </div>

            {/* Document Name */}
            <div>
              <Label>Document Name *</Label>
              <Input
                placeholder="Enter document name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Category */}
            <div>
              <Label>Category (Folder) *</Label>
              <Select
                value={uploadForm.category}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customs">📋 Customs Forms</SelectItem>
                  <SelectItem value="finance">💰 Finance Forms</SelectItem>
                  <SelectItem value="crm">👥 CRM Forms</SelectItem>
                  <SelectItem value="hr">👤 HR Forms</SelectItem>
                  <SelectItem value="operations">🚚 Operations Forms</SelectItem>
                  <SelectItem value="company_docs">🏢 Company Documents (per vertical)</SelectItem>
                  <SelectItem value="general">📁 General Forms</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Vertical + Doc Tag (only when category is company_docs) */}
            {uploadForm.category === "company_docs" && (
              <>
                <div>
                  <Label>Vertical (Company) *</Label>
                  <Select
                    value={uploadForm.vertical}
                    onValueChange={(value) => setUploadForm(prev => ({ ...prev, vertical: value }))}
                  >
                    <SelectTrigger data-testid="company-doc-vertical-select">
                      <SelectValue placeholder="Select vertical" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="europacific">🚢 Europacific</SelectItem>
                      <SelectItem value="cargo_cfs">📦 Cargo CFS</SelectItem>
                      <SelectItem value="ep_holding">🏛️ EP Holding</SelectItem>
                      <SelectItem value="ep_as">⚓ EP A&S</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Document Type *</Label>
                  <Select
                    value={uploadForm.doc_tag}
                    onValueChange={(value) => setUploadForm(prev => ({ ...prev, doc_tag: value }))}
                  >
                    <SelectTrigger data-testid="company-doc-tag-select">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_registry">Company Registry / Incorporation</SelectItem>
                      <SelectItem value="bank_details">Bank Account Details</SelectItem>
                      <SelectItem value="vat_cert">VAT Number Certificate</SelectItem>
                      <SelectItem value="eori">EORI Certificate</SelectItem>
                      <SelectItem value="aoeo_cert">AEO Certificate</SelectItem>
                      <SelectItem value="iso_cert">ISO Certificate</SelectItem>
                      <SelectItem value="insurance">Insurance Certificate</SelectItem>
                      <SelectItem value="tax_residency">Tax Residency Certificate</SelectItem>
                      <SelectItem value="license">License / Authorization</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Link to Company */}
            <div>
              <Label>Link to Company (optional)</Label>
              <Select
                value={uploadForm.linked_company_id || "__none__"}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, linked_company_id: value === "__none__" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Link to Contact */}
            <div>
              <Label>Link to Contact (optional)</Label>
              <Select
                value={uploadForm.linked_contact_id || "__none__"}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, linked_contact_id: value === "__none__" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.first_name} {contact.last_name} {contact.company_name && `(${contact.company_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUploadDialog(false);
              setSelectedFile(null);
              setUploadForm({ name: "", linked_company_id: "", linked_contact_id: "", category: "general", vertical: "", doc_tag: "" });
            }}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={uploading || !selectedFile || !uploadForm.name}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Upload Dialog (for drag/drop into folders) */}
      <Dialog open={showQuickUpload} onOpenChange={setShowQuickUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Name Your Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {quickUploadFile && (
              <div className="flex items-center gap-3 p-3 bg-accent/50 rounded-lg">
                <FileText className="h-8 w-8 text-indigo-500" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{quickUploadFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(quickUploadFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            )}
            
            <div>
              <Label>Document Name *</Label>
              <Input
                placeholder="Enter document name"
                value={quickUploadName}
                onChange={(e) => setQuickUploadName(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Will be saved to: <span className="font-medium capitalize">{quickUploadCategory} Forms</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowQuickUpload(false);
              setQuickUploadFile(null);
              setQuickUploadCategory(null);
              setQuickUploadName("");
            }}>
              Cancel
            </Button>
            <Button onClick={handleQuickUpload} disabled={uploading || !quickUploadName}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pantheon Import/Export Dialog */}
      <Dialog open={showPantheonDialog} onOpenChange={setShowPantheonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Pantheon Companies Sync
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Stats */}
            {pantheonStats && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">{pantheonStats.total_companies}</p>
                  <p className="text-xs text-slate-500">Total Companies</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{pantheonStats.pantheon_synced}</p>
                  <p className="text-xs text-green-600">Pantheon Synced</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{pantheonStats.not_synced}</p>
                  <p className="text-xs text-amber-600">Not in Pantheon</p>
                </div>
              </div>
            )}

            {/* Import Section */}
            <div className="border rounded-lg p-4 bg-blue-50/50">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Import from Pantheon
              </h4>
              <p className="text-sm text-blue-700 mb-3">
                Upload Pantheon export file (.xls XML format) to import companies into CRM.
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Select File</Label>
                  <Input
                    type="file"
                    accept=".xls,.xml"
                    onChange={(e) => setPantheonFile(e.target.files?.[0] || null)}
                    className="mt-1"
                  />
                  {pantheonFile && (
                    <p className="text-xs text-green-600 mt-1">Selected: {pantheonFile.name}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm">Import Mode</Label>
                  <Select value={pantheonImportMode} onValueChange={setPantheonImportMode}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip">Skip duplicates</SelectItem>
                      <SelectItem value="update">Update existing companies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={handlePantheonImport} 
                  disabled={!pantheonFile || pantheonImporting}
                  className="w-full"
                >
                  {pantheonImporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import Companies
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Export Section */}
            <div className="border rounded-lg p-4 bg-green-50/50">
              <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export to Pantheon Format
              </h4>
              <p className="text-sm text-green-700 mb-3">
                Download all CRM companies in Pantheon-compatible CSV format.
              </p>
              
              <Button 
                onClick={handlePantheonExport} 
                variant="outline"
                className="w-full border-green-300 text-green-700 hover:bg-green-100"
              >
                <Download className="h-4 w-4 mr-2" />
                Export to CSV
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPantheonDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Emails Dialog */}
      <Dialog open={showImportEmailsDialog} onOpenChange={setShowImportEmailsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Import Email Contacts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Import email addresses from different sources into CRM contacts.
            </p>
            
            <div className="space-y-3">
              <Label className="text-sm font-medium">Select Source</Label>
              <div className="grid gap-2 max-h-[350px] overflow-y-auto">
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  emailImportSource === "customer_advisory" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="emailSource"
                    value="customer_advisory"
                    checked={emailImportSource === "customer_advisory"}
                    onChange={(e) => setEmailImportSource(e.target.value)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Customer Advisory</p>
                    <p className="text-xs text-slate-500">Emails from offers@europacific.com</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  emailImportSource === "customer_groups" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="emailSource"
                    value="customer_groups"
                    checked={emailImportSource === "customer_groups"}
                    onChange={(e) => setEmailImportSource(e.target.value)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Customer Groups</p>
                    <p className="text-xs text-slate-500">Contacts from operations groups</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  emailImportSource === "offers_email" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="emailSource"
                    value="offers_email"
                    checked={emailImportSource === "offers_email"}
                    onChange={(e) => setEmailImportSource(e.target.value)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Offers Emails</p>
                    <p className="text-xs text-slate-500">Emails from offers/quotes database</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  emailImportSource === "newsletter_subscribers" ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="emailSource"
                    value="newsletter_subscribers"
                    checked={emailImportSource === "newsletter_subscribers"}
                    onChange={(e) => setEmailImportSource(e.target.value)}
                    className="h-4 w-4 text-purple-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">Newsletter Subscribers</p>
                    <p className="text-xs text-slate-500">Active newsletter subscribers list</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  emailImportSource === "hr_staff" ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input
                    type="radio"
                    name="emailSource"
                    value="hr_staff"
                    checked={emailImportSource === "hr_staff"}
                    onChange={(e) => setEmailImportSource(e.target.value)}
                    className="h-4 w-4 text-green-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">HR / Staff Directory</p>
                    <p className="text-xs text-slate-500">Internal staff email addresses</p>
                  </div>
                </label>
              </div>
            </div>
            
            <Button 
              onClick={handleImportEmails}
              disabled={importingEmails}
              className="w-full"
            >
              {importingEmails ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Import Contacts
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={(open) => {
        setShowCreateGroupDialog(open);
        if (!open) {
          setEditingGroup(null);
          setGroupName("");
          setGroupDescription("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingGroup ? <Edit className="h-5 w-5 text-indigo-600" /> : <Plus className="h-5 w-5 text-indigo-600" />}
              {editingGroup ? "Edit Group" : "Create New Group"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Group Name *</Label>
              <Input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., IBL + Bansard"
                className="mt-1"
                data-testid="group-name-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use to group related Pantheon companies (same company, different offices)
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Textarea
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Optional description..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateGroupDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateGroup}
                disabled={savingGroup || !groupName.trim()}
                className="flex-1"
              >
                {savingGroup ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {editingGroup ? "Update Group" : "Create Group"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={(open) => {
        setShowAddMemberDialog(open);
        if (!open) {
          setMemberCompanySearch("");
          setMemberSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              Add Company to Group
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Search Companies</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={memberCompanySearch}
                  onChange={(e) => {
                    setMemberCompanySearch(e.target.value);
                    searchMemberCompanies(e.target.value);
                  }}
                  placeholder="Search Pantheon companies..."
                  className="pl-10"
                  data-testid="member-search-input"
                />
                {searchingMembers && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
              </div>
            </div>
            
            {/* Search Results */}
            {memberSearchResults.length > 0 && (
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {memberSearchResults.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => handleAddMember(company)}
                    disabled={addingMember}
                    className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors border-b last:border-b-0"
                  >
                    <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{company.name}</p>
                      <p className="text-xs text-slate-500">
                        {company.country || "No country"}
                        {company.accounting_ref && ` • Pantheon: ${company.accounting_ref}`}
                      </p>
                    </div>
                    <Plus className="h-4 w-4 text-indigo-600" />
                  </button>
                ))}
              </div>
            )}
            
            {memberCompanySearch.length >= 2 && memberSearchResults.length === 0 && !searchingMembers && (
              <div className="text-center py-4 text-slate-500">
                <p>No companies found matching "{memberCompanySearch}"</p>
              </div>
            )}
            
            {memberCompanySearch.length < 2 && (
              <div className="text-center py-4 text-slate-400">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Type at least 2 characters to search</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
